import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const OTP_CSRF = 'hJDRTxO0QPa8awJt59YYXdVlKlk0DwXtF57wT35gBaCq9YfXgQ6lPe4NNUkHHN13';

// --- ANIMATION COMPONENTS ---

const FloatingParticle = ({ delay, duration, x, y, size }) => (
  <motion.div
    className="absolute rounded-full bg-white/20 backdrop-blur-sm"
    style={{
      width: size,
      height: size,
      left: x,
      top: y
    }}
    animate={{
      y: [0, -20, 0],
      x: [0, 10, 0],
      opacity: [0.3, 0.6, 0.3],
    }}
    transition={{
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay,
    }}
  />
);

const AntiGravityBackground = () => {
  const particles = useMemo(() => {
    return [...Array(15)].map((_, i) => ({
      id: i,
      size: Math.random() * 60 + 20,
      x: `${Math.random() * 100}%`,
      y: `${Math.random() * 100}%`,
      duration: Math.random() * 5 + 5,
      delay: Math.random() * 2,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <FloatingParticle key={p.id} {...p} />
      ))}
    </div>
  );
};

// --- MAIN COMPONENT ---

// --- UI HELPERS ---
const InputField = ({ id, label, error, type = 'text', ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <div className="w-full">
      <label htmlFor={id} className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          className={`w-full bg-transparent border-b ${error ? 'border-red-400' : 'border-gray-300'} py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-black focus:outline-none transition-colors ${isPassword ? 'pr-8' : ''}`}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-0 top-2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        )}
      </div>
      {error && <p className="mt-1 text-[10px] text-red-500">{error}</p>}
    </div>
  );
};

// --- MAIN COMPONENT ---

export default function Login() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('credentials'); // 'credentials' | 'phone'
  const [credForm, setCredForm] = useState({ identifier: '', password: '' });
  const [phone, setPhone] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // OTP state
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [otpNotice, setOtpNotice] = useState({ type: '', text: '' });

  const OTP_TTL_SECONDS = 300;

  // --- VALIDATION ---
  const credErrors = useMemo(() => {
    const e = {};
    if (!credForm.identifier.trim()) e.identifier = 'Required';
    if (!credForm.password) e.password = 'Required';
    return e;
  }, [credForm]);

  const phoneError = useMemo(() => {
    if (!phoneTouched) return '';
    if (!phone.trim()) return 'Required';
    if (phone.replace(/\D/g, '').length < 9) return 'Invalid number';
    return '';
  }, [phone, phoneTouched]);

  // --- OTP LOGIC ---
  const startOtpCountdown = () => setOtpSecondsLeft(OTP_TTL_SECONDS);

  useEffect(() => {
    if (!otpOpen || otpSecondsLeft <= 0) return;
    const id = setInterval(() => setOtpSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [otpOpen, otpSecondsLeft]);

  const formatMMSS = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleCredChange = (key) => (e) => {
    setCredForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const setAuthFromResponse = (data) => {
    try {
      if (data?.access) localStorage.setItem('emba_access', data.access);
      if (data?.refresh) localStorage.setItem('emba_refresh', data.refresh);
      // Store user object. If data has a 'user' key, use that. Otherwise use data itself if it looks like a user object.
      // The login response has 'user' key. The by-phone response has 'user' key.
      if (data?.user) {
        localStorage.setItem('emba_user', JSON.stringify(data.user));
      } else if (data?.id) {
        localStorage.setItem('emba_user', JSON.stringify(data));
      }
    } catch { }
  };

  // --- ACTIONS ---
  const handleCredSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setSuccessMessage('');

    if (Object.keys(credErrors).length > 0) return;

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/user-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFTOKEN': OTP_CSRF
        },
        body: JSON.stringify(credForm),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || data?.message || 'Login failed');

      setAuthFromResponse(data);
      setSuccessMessage('Login successful. Redirecting...');
      setTimeout(() => navigate('/'), 1000);
    } catch (err) {
      setServerError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    const raw = phone.trim();
    if (!raw) throw new Error('Enter phone number');
    setOtpLoading(true);
    try {
      // 1. Check if phone exists
      const checkRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/phone-number-exist/?phone=${encodeURIComponent(raw)}`);
      const checkData = await checkRes.json().catch(() => ({}));

      if (!checkData.exists) {
        throw new Error('Account not found with this phone number');
      }

      // 2. Send OTP
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/otp/send/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFTOKEN': OTP_CSRF },
        body: JSON.stringify({ phone_number: raw, purpose: 'general' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) throw new Error(data?.message || 'Failed to send OTP');

      setOtpDigits(['', '', '', '', '', '']);
      setOtpOpen(true);
      setOtpNotice({ type: '', text: '' });
      startOtpCountdown();
    } catch (err) {
      setServerError(String(err?.message || err));
      throw err; // Re-throw to stop execution if needed
    } finally {
      setOtpLoading(false);
    }
  };

  const loginByPhone = async (normalizedPhone) => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/by-phone/?phone_number=${encodeURIComponent(normalizedPhone)}`;
    const res = await fetch(url, { method: 'GET', headers: { accept: 'application/json' } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.status === false) throw new Error(data?.message || data?.detail || 'No account found');

    setAuthFromResponse(data);
    setOtpOpen(false);
    setSuccessMessage('Login successful. Redirecting...');
    setTimeout(() => navigate('/'), 1200);
  };

  const verifyOtpAndLogin = async () => {
    const code = otpDigits.join('');
    if (code.length !== 6) throw new Error('Enter 6-digit code');
    setOtpLoading(true);
    try {
      // 1. Verify OTP
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/otp/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFTOKEN': OTP_CSRF },
        body: JSON.stringify({ phone_number: phone.trim(), purpose: 'general', code }),
      });
      const data = await res.json().catch(() => ({}));
      const verified = res.ok && (data?.is_verified === true || data?.status === true);

      if (!verified) {
        const msg = data?.message || data?.detail || 'Invalid OTP';
        setOtpNotice({ type: 'error', text: msg });
        throw new Error(msg);
      }

      setOtpNotice({ type: 'success', text: 'Verified. Logging in...' });

      // 2. Get User Details
      const userRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointment-users/by-phone/?phone=${encodeURIComponent(phone.trim())}`, {
        method: 'GET',
        headers: { accept: 'application/json' }
      });
      const userData = await userRes.json().catch(() => ({}));

      if (!userRes.ok || userData?.status === false) {
        throw new Error(userData?.message || 'Failed to retrieve user details');
      }

      setAuthFromResponse(userData);
      setOtpOpen(false);
      setSuccessMessage('Login successful. Redirecting...');
      setTimeout(() => navigate('/'), 1000);

    } catch (err) {
      setOtpNotice({ type: 'error', text: String(err?.message || err) });
    } finally {
      setOtpLoading(false);
    }
  };



  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">

      {/* LEFT PANEL - VISUAL */}
      <div className="relative w-full md:w-5/12 lg:w-1/2 h-64 md:h-auto overflow-hidden bg-black">
        <img
          src="https://skyphaseobeng.s3.eu-north-1.amazonaws.com/casual3.JPG"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
        <AntiGravityBackground />

        <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-12 text-white z-10">
          <div>
            <Link to="/" className="text-xs tracking-[0.4em] font-bold hover:opacity-80 transition-opacity">
              EMBA BY EMEFA
            </Link>
          </div>
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight mb-4"
            >
              Welcome <br /> Back.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-sm tracking-wider opacity-80 max-w-xs font-light"
            >
              Sign in to manage your appointments and view your exclusive collections.
            </motion.p>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL - FORM */}
      <div className="relative z-20 w-full md:w-7/12 lg:w-1/2 flex items-center justify-center p-6 md:p-12 lg:p-20 overflow-y-auto bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <h1 className="text-2xl font-serif text-gray-900 mb-2">Sign In</h1>
            <p className="text-sm text-gray-500">Access your account.</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex mb-8 border-b border-gray-100">
            <button
              onClick={() => { setActiveTab('credentials'); setServerError(''); }}
              className={`pb-3 text-xs tracking-widest uppercase mr-6 transition-colors ${activeTab === 'credentials' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              Password
            </button>
            <button
              onClick={() => { setActiveTab('phone'); setServerError(''); }}
              className={`pb-3 text-xs tracking-widest uppercase transition-colors ${activeTab === 'phone' ? 'border-b-2 border-black text-black' : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              Phone
            </button>
          </div>

          {/* Alerts */}
          <AnimatePresence>
            {serverError && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 p-3 bg-red-50 text-red-600 text-xs tracking-wide border-l-2 border-red-500">
                {serverError}
              </motion.div>
            )}
            {successMessage && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 p-3 bg-green-50 text-green-600 text-xs tracking-wide border-l-2 border-green-500">
                {successMessage}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Credentials Form */}
          {activeTab === 'credentials' && (
            <motion.form
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
              onSubmit={handleCredSubmit}
            >
              <InputField
                id="username"
                label="Username"
                placeholder="Enter username"
                value={credForm.identifier}
                onChange={handleCredChange('identifier')}
                error={credErrors.identifier && credForm.identifier === '' ? credErrors.identifier : ''}
              />
              <InputField
                id="password"
                label="Password"
                type="password"
                placeholder="Enter password"
                value={credForm.password}
                onChange={handleCredChange('password')}
                error={credErrors.password && credForm.password === '' ? credErrors.password : ''}
              />

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-black text-white py-4 text-xs tracking-[0.2em] uppercase hover:bg-gray-900 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
              </div>
            </motion.form>
          )}

          {/* Phone Form */}
          {activeTab === 'phone' && (
            <motion.form
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
              onSubmit={(e) => e.preventDefault()}
            >
              <InputField
                id="phone"
                label="Phone Number"
                type="tel"
                placeholder="0242123456"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => setPhoneTouched(true)}
                error={phoneError}
              />
              <p className="text-[10px] text-gray-400">We'll send a code to verify it's you.</p>

              <div className="pt-4">
                <button
                  type="button"
                  disabled={otpLoading || !phone.trim()}
                  onClick={async () => {
                    setPhoneTouched(true);
                    if (phoneError) return;
                    setServerError('');
                    setOtpNotice({ type: '', text: '' });
                    try { await sendOtp(); } catch (err) { setServerError(String(err?.message || err)); }
                  }}
                  className="w-full bg-black text-white py-4 text-xs tracking-[0.2em] uppercase hover:bg-gray-900 transition-colors disabled:opacity-50"
                >
                  {otpLoading ? 'Sending Code...' : 'Send Code'}
                </button>
              </div>
            </motion.form>
          )}

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              New here?{' '}
              <Link to="/sign-up" className="text-black font-medium underline underline-offset-4">
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      <AnimatePresence>
        {otpOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm bg-white p-8 shadow-2xl"
            >
              <h3 className="text-xl font-serif text-center mb-2">Verify Phone</h3>
              <p className="text-xs text-center text-gray-500 mb-6">
                Enter the code sent to <span className="font-bold text-black">{phone}</span>
              </p>

              <div className="flex justify-center gap-2 mb-6">
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    type="tel"
                    maxLength={1}
                    className="w-10 h-12 border-b-2 border-gray-200 text-center text-xl focus:border-black focus:outline-none transition-colors"
                    value={d}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                      setOtpDigits(arr => { const n = [...arr]; n[i] = v; return n; });
                      if (v && i < 5) e.target.parentElement.querySelectorAll('input')[i + 1]?.focus();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
                        e.currentTarget.parentElement.querySelectorAll('input')[i - 1]?.focus();
                      }
                    }}
                  />
                ))}
              </div>

              {otpNotice.text && (
                <p className={`text-center text-xs mb-4 ${otpNotice.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>
                  {otpNotice.text}
                </p>
              )}

              <div className="flex justify-between items-center mb-6 text-[10px] text-gray-400 uppercase tracking-wider">
                <span>{formatMMSS(otpSecondsLeft)} remaining</span>
                <button onClick={sendOtp} disabled={otpLoading} className="hover:text-black underline">Resend</button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={async () => {
                    setOtpNotice({ type: '', text: '' });
                    try { await verifyOtpAndLogin(); } catch { }
                  }}
                  disabled={otpLoading}
                  className="w-full bg-black text-white py-3 text-xs tracking-[0.2em] uppercase hover:bg-gray-900"
                >
                  {otpLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  onClick={() => setOtpOpen(false)}
                  className="w-full border border-gray-200 py-3 text-xs tracking-[0.2em] uppercase hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}